import React, { useCallback, useEffect, useRef, useState } from 'react';
import GetAppDesktop from './GetAppDesktop';
import GetAppMobile from './GetAppMobile';
import DeepLinkCopiedModal from '../Donee/components/CheckoutWizard/ThankYouWizard/DeepLinkCopiedModal';
import { usePushEvents } from '../../hooks/PushEvents';
import {
    appDownloadEvent,
    clickEvent,
    successEvent,
    viewEvent,
} from '../../hooks/PushEvents/clevertapEvents';
import { isAppleDevice } from '../Donee/components/common/utils';
import { useDeepLink } from '../../hooks/useDeepLink';
import { useDonee, useDonorProfile, useGiftSummary } from '../../api';
import { Dialog, useMediaQuery, useTheme } from '@mui/material';
import { useFeatureFlag } from '../../providers';
import { FeatureFlag } from '../../constants';

const breakpoint = 800;
export const isMobile = window.innerWidth < breakpoint;

const postEventPayload = {
    name: 'Get app modal',
    location: 'historymodal',
    action: 'completed',
    voucher: 'DDL voucher',
};

const eventPayload = {
    location: 'historymodal',
    action: 'clicked',
};

const appStoreLink =
    'https://apps.apple.com/app/apple-store/id725815127?pt=8388804&ct=oga_appdownload_historymodal&mt=8';
const playStoreLink =
    'https://play.google.com/store/apps/details?id=com.pushcontrolz.givelify&referrer=utm_source%3Doga%26utm_medium%3Dweb%26utm_campaign%3Doga_appdownload_historymodal';
export const getMobileAppLink = () => {
    // copyDoneeIdToClipboard();
    window.location.href = isAppleDevice() ? appStoreLink : playStoreLink;
};

const deepLinkTitle = (
    <>
        We’ve <span className="deep-link-copied-text">copied</span> a link
        <br />
        to your giving history
    </>
);

interface GetAppModalContentProps {
    handleDownload: () => unknown;
    handleClose?: () => unknown;
}

export const GetAppModalContent: React.FC<GetAppModalContentProps> = ({
    handleClose,
    handleDownload,
}) => {
    const [url, setUrl] = useState<string | undefined>(undefined);
    const { donee } = useDonee();
    const { donorProfile } = useDonorProfile();
    const { deepLinkState, getDeepLink, showCopiedModal, setShowCopiedModal } =
        useDeepLink(undefined, eventPayload, handleDownload);

    const handleDownloadApp = useCallback(() => {
        getDeepLink(donee, donorProfile, '/dl/history', undefined).then((s) => {
            s && setUrl(s);
        });
    }, [setUrl, donee, donorProfile, getDeepLink]);

    return (
        <div>
            <GetAppMobile
                isLoading={deepLinkState.loading}
                isDisabled={Boolean(deepLinkState.voucher)}
                handleDownload={handleDownloadApp}
                onClose={handleClose}
            />
            <GetAppDesktop onClose={handleClose} />
            <DeepLinkCopiedModal
                url={url}
                open={showCopiedModal}
                title={deepLinkTitle}
                viewEvent={viewEvent}
                clickEvent={clickEvent}
                successEvent={successEvent}
                clickEventPayload={postEventPayload}
                onClick={handleDownload}
                onClose={() => setShowCopiedModal(false)}
            />
        </div>
    );
};

interface GetAppModalProps {
    open: boolean;
    setOpen: (o: boolean) => unknown;
}

const GetAppModal: React.FC<GetAppModalProps> = ({ open, setOpen }) => {
    const { pushEvent } = usePushEvents();
    const theme = useTheme();
    const goFullscreen = useMediaQuery(theme.breakpoints.down(640));
    const { giftSummary: gift } = useGiftSummary();
    const { isFeatureEnabled } = useFeatureFlag();
    // StrictMode re-runs mount effects in development; refs survive that, so
    // this keeps "shown" to one event per open
    const shownEventSent = useRef(false);

    useEffect(() => {
        if (!open) {
            shownEventSent.current = false;
            return;
        }
        if (shownEventSent.current) return;
        shownEventSent.current = true;
        const flagEnabled = isFeatureEnabled(FeatureFlag.OGA_APPHISTORY);
        const giftCount = gift?.all.count;
        pushEvent(appDownloadEvent, {
            location: 'historymodal',
            action: 'shown',
            'oga-apphistory':
                flagEnabled && giftCount === 1 ? 'variation' : 'control',
        });
        // run only once when open is changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleClose = useCallback(() => {
        document.body.classList.remove('ReactModal__Body--open');
        setOpen(false);
        const flagEnabled = isFeatureEnabled(FeatureFlag.OGA_APPHISTORY);
        const giftCount = gift?.all.count;
        pushEvent(appDownloadEvent, {
            location: 'historymodal',
            action: 'dismissed',
            'oga-apphistory':
                flagEnabled && giftCount === 1 ? 'variation' : 'control',
        });
    }, [gift?.all.count, isFeatureEnabled, pushEvent, setOpen]);

    const handleDownload = useCallback(() => {
        const flagEnabled = isFeatureEnabled(FeatureFlag.OGA_APPHISTORY);
        const giftCount = gift?.all.count;
        pushEvent(appDownloadEvent, {
            location: 'historymodal',
            action: 'clicked',
            'oga-apphistory':
                flagEnabled && giftCount === 1 ? 'variation' : 'control',
        });
        getMobileAppLink();
    }, [gift?.all.count, isFeatureEnabled, pushEvent]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            container={document.getElementById('root')}
            sx={{
                '& .MuiBackdrop-root': { backgroundColor: '#f1f0f7cc' },
                '& .MuiDialog-paper': {
                    borderRadius: goFullscreen ? 0 : '32px',
                    padding: 0,
                    backgroundColor: '#ffffff',
                    boxShadow:
                        '0 1px 6px 0 rgba(10, 0, 77, 0.04), 0 5.5px 30px 0 rgba(10, 0, 77, 0.05)',
                    minWidth: 320,
                    mx: 0,
                    overflow: 'hidden',
                },
            }}
            fullScreen={goFullscreen}
        >
            <GetAppModalContent
                handleClose={handleClose}
                handleDownload={handleDownload}
            />
        </Dialog>
    );
};

export default GetAppModal;
